import { forwardRef } from 'react';
import Snackbar, { type SnackbarProps } from '@mui/material/Snackbar';
import Alert, { type AlertProps } from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

export interface AppSnackbarProps extends Omit<SnackbarProps, 'children'> {
  /**
   * Message to display
   */
  message: React.ReactNode;
  /**
   * Severity determines the color and icon
   */
  severity?: 'success' | 'error' | 'warning' | 'info';
  /**
   * Show close button
   */
  showCloseButton?: boolean;
  /**
   * Callback when snackbar should close
   */
  onClose?: () => void;
  /**
   * Variant of the alert
   */
  variant?: 'filled' | 'standard';
}

const severityIcons: Record<string, React.ReactElement> = {
  success: <CheckCircleIcon fontSize="small" />,
  error: <ErrorIcon fontSize="small" />,
  warning: <WarningIcon fontSize="small" />,
  info: <InfoIcon fontSize="small" />,
};

/**
 * AppSnackbar - Toast notification component
 *
 * Wraps MUI Snackbar + Alert with our design system patterns.
 * Use for feedback messages, success/error states.
 */
export const AppSnackbar = forwardRef<HTMLDivElement, AppSnackbarProps>(
  (
    {
      message,
      severity = 'info',
      showCloseButton = true,
      onClose,
      variant = 'filled',
      autoHideDuration = 4000,
      anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
      ...props
    },
    ref
  ) => {
    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      onClose?.();
    };

    return (
      <Snackbar
        ref={ref}
        autoHideDuration={autoHideDuration}
        anchorOrigin={anchorOrigin}
        onClose={handleClose}
        {...props}
      >
        <Alert
          severity={severity}
          variant={variant}
          icon={severityIcons[severity]}
          action={
            showCloseButton && onClose ? (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={onClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : undefined
          }
          sx={{
            width: '100%',
            minWidth: 320,
            maxWidth: 568,
            alignItems: 'center',
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    );
  }
);

AppSnackbar.displayName = 'AppSnackbar';
